// frontend/src/pages/admin/AdminBulkDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { bulkApi } from '../../api/bulk';
import helpers from '../../utils/helpers';
import { FaArrowLeft, FaUser, FaEnvelope, FaPaperPlane, FaSpinner, FaCheckCircle, FaPhoneAlt } from 'react-icons/fa';

function AdminBulkDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Quote Form State
  const [quoteItems, setQuoteItems] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');

  const fetchRequest = useCallback(async () => {
    try {
      const data = await bulkApi.getRequestById(requestId);
      setRequest(data);
      setQuoteItems(data.items.map(item => ({
        ...item,
        quoted_unit_price: item.quoted_unit_price || ''
      })));
      setAdminNotes(data.admin_notes || '');
      setValidUntil(data.valid_until || '');
    } catch (err) {
      console.error("Failed to load request", err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handlePriceChange = (index, value) => {
    const updated = [...quoteItems];
    updated[index].quoted_unit_price = value;
    setQuoteItems(updated);
  };

  const calculateTotal = () => {
    return quoteItems.reduce((sum, item) => {
      const price = parseFloat(item.quoted_unit_price) || 0;
      return sum + (price * item.quantity);
    }, 0);
  };

  const handleSendQuote = async (e) => {
    e.preventDefault();
    if (quoteItems.some(i => i.quoted_unit_price === '' || i.quoted_unit_price === null)) {
      alert("Please enter a unit price for all items (enter 0 if unavailable/free).");
      return;
    }
    setSending(true);
    try {
      const payload = {
        items: quoteItems.map(i => ({
          ...i,
          quoted_unit_price: parseFloat(i.quoted_unit_price)
        })),
        admin_notes: adminNotes,
        valid_until: validUntil
      };
      await bulkApi.sendQuoteAdmin(requestId, payload);
      alert("Quote sent successfully!");
      navigate('/admin/bulk');
    } catch (err) {
      alert("Failed to send quote.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <FaSpinner className="animate-spin text-4xl text-primary" />
    </div>
  );
  if (!request) return <div className="text-center py-20">Request not found.</div>;

  const isEditable = request.status === 'pending' || request.status === 'quoted';
  const displayName = request.user_name || 'Unknown User';
  // Older requests may still hold a phone sign-up's placeholder address.
  const contactEmail = (request.user_email || '').toLowerCase().endsWith('@phone.user') ? '' : request.user_email;
  const verifiedTag = (flag) => {
    if (flag === true) return <span className="ml-1 text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">verified</span>;
    if (flag === false) return <span className="ml-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">not verified</span>;
    return null;
  };

  return (
    <div className="bg-gray-50 min-h-screen p-3 sm:p-6">

      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Link to="/admin/bulk" className="text-gray-500 hover:text-primary flex items-center gap-1 text-sm">
          <FaArrowLeft /> Back
        </Link>
        <h1 className="text-lg sm:text-2xl font-bold text-gray-800">
          Quote Request: {request.request_id}
        </h1>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          request.status === 'accepted' ? 'bg-green-100 text-green-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {request.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">

        {/* LEFT: QUOTE BUILDER */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-bold text-gray-700">Requested Items</h3>
              <span className="text-sm text-gray-500">{quoteItems.length} Items</span>
            </div>

            <form onSubmit={handleSendQuote}>

              {/* MOBILE: Card layout per item */}
              <div className="block sm:hidden divide-y divide-gray-100">
                {quoteItems.map((item, idx) => (
                  <div key={idx} className="p-4 space-y-3">
                    <div>
                      <p className="font-bold text-gray-800">{item.part_name}</p>
                      {item.notes && <p className="text-xs text-gray-500 italic mt-1">{item.notes}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-xs text-gray-400 uppercase mb-1">Quantity</p>
                        <p className="font-semibold text-gray-700">{item.quantity} <span className="text-xs text-gray-400">{item.unit}</span></p>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-xs text-gray-400 uppercase mb-1">Target Price</p>
                        <p className="font-semibold text-gray-700">{item.target_price ? helpers.formatCurrency(item.target_price, 'NGN') : '-'}</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                        Unit Price (NGN)
                      </label>
                      <input
                        type="number"
                        value={item.quoted_unit_price}
                        onChange={(e) => handlePriceChange(idx, e.target.value)}
                        className="w-full p-3 border-2 border-blue-300 rounded-lg text-right font-bold text-blue-700 focus:ring-2 focus:ring-blue-500 outline-none text-base"
                        placeholder="0.00"
                        disabled={!isEditable}
                        min="0"
                      />
                    </div>

                    <div className="flex justify-between items-center bg-blue-50 rounded p-2">
                      <span className="text-xs font-bold text-blue-800 uppercase">Line Total</span>
                      <span className="font-bold text-blue-900">
                        {helpers.formatCurrency((parseFloat(item.quoted_unit_price) || 0) * item.quantity, 'NGN')}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Grand Total mobile */}
                <div className="p-4 bg-blue-50 flex justify-between items-center">
                  <span className="font-bold text-blue-900 uppercase text-sm">Grand Total</span>
                  <span className="font-bold text-blue-900 text-lg">
                    {helpers.formatCurrency(calculateTotal(), 'NGN')}
                  </span>
                </div>
              </div>

              {/* DESKTOP: Table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-100 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="p-4">Item Details</th>
                      <th className="p-4 text-center">Qty</th>
                      <th className="p-4 text-right">Target Price</th>
                      <th className="p-4 w-40">Unit Price (NGN)</th>
                      <th className="p-4 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quoteItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="p-4">
                          <div className="font-bold text-gray-800">{item.part_name}</div>
                          {item.notes && <div className="text-xs text-gray-500 italic">{item.notes}</div>}
                        </td>
                        <td className="p-4 text-center">
                          {item.quantity} <span className="text-xs text-gray-500 block">{item.unit}</span>
                        </td>
                        <td className="p-4 text-right text-gray-500">
                          {item.target_price ? helpers.formatCurrency(item.target_price, 'NGN') : '-'}
                        </td>
                        <td className="p-4">
                          <input
                            type="number"
                            value={item.quoted_unit_price}
                            onChange={(e) => handlePriceChange(idx, e.target.value)}
                            className="w-full p-2 border rounded text-right font-bold text-blue-700 focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="0.00"
                            disabled={!isEditable}
                            min="0"
                          />
                        </td>
                        <td className="p-4 text-right font-bold text-gray-800">
                          {helpers.formatCurrency((parseFloat(item.quoted_unit_price) || 0) * item.quantity, 'NGN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-blue-50">
                    <tr>
                      <td colSpan="4" className="p-4 text-right font-bold text-blue-900 uppercase">Grand Total:</td>
                      <td className="p-4 text-right font-bold text-blue-900 text-xl">
                        {helpers.formatCurrency(calculateTotal(), 'NGN')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Admin Inputs */}
              <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Admin Notes (Visible to User)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="w-full p-3 border rounded focus:ring-2 focus:ring-primary outline-none"
                      rows="3"
                      placeholder="e.g. Prices include VAT. Delivery within 3 days."
                      disabled={!isEditable}
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Quote Valid Until</label>
                    <input
                      type="date"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      className="w-full p-3 border rounded focus:ring-2 focus:ring-primary outline-none"
                      disabled={!isEditable}
                    />
                  </div>
                </div>

                {isEditable && (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded shadow-md flex items-center justify-center gap-2 transition-transform transform hover:-translate-y-1"
                    >
                      {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                      {request.status === 'quoted' ? 'Update Quote' : 'Send Quote'}
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT: CUSTOMER INFO */}
        <div className="lg:col-span-1 space-y-4 sm:space-y-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">Customer Details</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-xl flex-shrink-0">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-gray-800 text-lg truncate">{displayName}</p>
                {request.company_name && <p className="text-xs text-gray-500 truncate">{request.company_name}</p>}
              </div>
            </div>
            <div className="space-y-2 mb-4 text-sm">
              <p className="flex items-center gap-2 min-w-0">
                <FaPhoneAlt className="text-gray-400 flex-shrink-0" />
                {request.user_phone
                  ? <><a href={`tel:${request.user_phone}`} className="text-blue-600 hover:underline truncate">{request.user_phone}</a>{verifiedTag(request.phone_verified)}</>
                  : <span className="text-gray-400 italic">No phone on this request</span>}
              </p>
              <p className="flex items-center gap-2 min-w-0">
                <FaEnvelope className="text-gray-400 flex-shrink-0" />
                {contactEmail
                  ? <><a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline truncate">{contactEmail}</a>{verifiedTag(request.email_verified)}</>
                  : <span className="text-gray-400 italic">No email on this request</span>}
              </p>
            </div>
            <Link to={`/admin/users?search=${request.account_email || request.user_email || ''}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <FaUser /> View Full Profile
            </Link>
          </div>

          {request.user_notes && (
            <div className="bg-yellow-50 p-4 sm:p-6 rounded-lg shadow-sm border border-yellow-200">
              <h3 className="text-sm font-bold text-yellow-800 uppercase mb-2">Customer Notes</h3>
              <p className="text-yellow-900 text-sm italic">"{request.user_notes}"</p>
            </div>
          )}

          {request.status === 'accepted' && (
            <div className="bg-green-100 p-4 sm:p-6 rounded-lg shadow-sm border border-green-200 text-center">
              <FaCheckCircle className="text-green-600 text-4xl mx-auto mb-3" />
              <h3 className="font-bold text-green-800 text-lg">Quote Accepted!</h3>
              <p className="text-green-700 text-sm mb-4">The customer has accepted this quote.</p>
              <button className="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700">
                Generate Invoice
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default AdminBulkDetail;